import { Google, AccountBalance, AutoGraph, Group, Security, CheckCircleOutline, SupportAgent, CloudDone } from '@mui/icons-material';
import { Button, createTheme, ThemeProvider, TextField, CircularProgress, Alert, Divider, Box, Typography } from '@mui/material';
import { getAuth } from 'firebase/auth';
import React, { useEffect, useState } from 'react';
import {
    createBrowserRouter,
    RouterProvider,
    Navigate,
} from "react-router-dom";
import './App.css';
import SidebarLayout from './components/SidebarLayout';
import { useTheme } from './context/ThemeContext';
import { firebaseGoogleSignIn, firebaseEmailSignIn, firebaseEmailSignUp } from './firebase/authHandler';
import { getPreferences } from './firebase/dataHandler';
import AccountDetail from './pages/accountDetail';
import Accounts from './pages/accounts';
import AddCustomer from './pages/addCustomer';
import Clients from './pages/clients';
import CustomerDetail from './pages/customerDetail';
import Dashboard from './pages/dashboard';
import Home from './pages/home';
import Profile from './pages/profile';
import Support from './pages/support';
import Subscription from './pages/subscription';
import Reports from './pages/Reports';
import Terms from './pages/terms';
import PrivacyPolicy from './pages/privacy';
import ContactUs from './pages/contact';
import CustomerConsole from './pages/CustomerConsole';
import CustomerLogin from './pages/CustomerLogin';
import CustomerDashboard from './pages/CustomerDashboard';
import CookieConsent from './components/CookieConsent';
import InvoicePage from './pages/InvoicePage';
import Invoices from './pages/Invoices';
import Trash from './pages/Trash';
import AdBanner from './components/AdBanner';
import RequireConsent from './components/RequireConsent';

function App() {
  const [user, setUser] = useState(getAuth().currentUser)
  const { theme } = useTheme();

  useEffect(() => {
    // Listener for authentication state changes
    const unsubscribe = getAuth().onAuthStateChanged((authUser) => {
      if (authUser) {
        // User is signed in
        setUser(authUser);
        getPreferences().then(prefs => {
          if (prefs?.language) {
            import('i18next').then(i18next => i18next.default.changeLanguage(prefs.language));
          }
        }).catch(console.error);
      } else {
        // No user is signed in
        setUser(null);
      }
    });

    return () => unsubscribe(); // Clean up the listener on unmount
  }, []);


  const handleGoogleSignIn = () =>{
    firebaseGoogleSignIn().then(user=>{
      setUser(user)
    })
  }

  const FeatureItem = ({ text, highlight }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <CheckCircleOutline sx={{ color: highlight ? 'var(--accent-green)' : 'var(--text-muted)', fontSize: 18 }} />
      <Typography sx={{ color: highlight ? 'var(--text-primary)' : 'var(--text-secondary)', fontSize: 14, fontWeight: highlight ? 600 : 400, fontFamily: 'Inter' }}>
        {text}
      </Typography>
    </Box>
  );

  const SignInPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSignUp, setIsSignUp] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleEmailAuth = async (e) => {
      e.preventDefault();
      if (!email || !password) {
        setError('Please enter both email and password');
        return;
      }
      setLoading(true);
      setError(null);
      try {
        if (isSignUp) {
          const u = await firebaseEmailSignUp(email, password);
          setUser(u);
        } else {
          const u = await firebaseEmailSignIn(email, password);
          setUser(u);
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
      }
      setLoading(false);
    };

    return (
      <Box sx={{
        display: 'flex',
        minHeight: '100vh',
        background: 'var(--bg-base)',
        flexDirection: { xs: 'column', md: 'row' }
      }}>
        {/* Left Side: Landing Content */}
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          padding: { xs: 4, md: 8 },
          background: 'var(--bg-gradient)',
          color: 'var(--text-primary)'
        }}>
          <Box sx={{ maxWidth: 680, margin: 'auto 0', width: '100%', py: { xs: 4, md: 6 } }}>
            {/* Branding / Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 6 }}>
              <img src={theme === 'light' ? require('./assets/site/long- squar-logo-fin-dark.svg').default : require('./assets/site/long- squar-logo-fin-light.svg').default} alt="Finaculator" style={{ height: '48px', width: 'auto' }} />
            </Box>
            
            {/* Hero Text */}
            <Typography sx={{ fontSize: { xs: 36, md: 54 }, fontWeight: 800, lineHeight: 1.15, mb: 3, letterSpacing: '-0.03em', fontFamily: 'Inter' }}>
              Master your <span style={{ color: 'var(--accent-green)' }}>microfinance</span> & daily collections.
            </Typography>
            
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: { xs: 16, md: 20 }, lineHeight: 1.6, mb: 6, fontFamily: 'Inter', maxWidth: '600px' }}>
              Replace chaotic spreadsheets with a centralized ledger. Streamline daily recoveries, track defaults instantly, and automate your entire loan cycle with a robust, cloud-based platform.
            </Typography>
            
            {/* Features Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4, mb: 8 }}>
              {/* Feature 1 */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 2, background: 'rgba(21,195,88,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-green)' }}>
                    <AccountBalance sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography sx={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 600, fontFamily: 'Inter' }}>Real-Time Ledger</Typography>
                </Box>
                <Typography sx={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, fontFamily: 'Inter' }}>
                  Never lose track of a single rupee. Get a live, bird's-eye view of daily collections, outstanding principals, and overall portfolio health.
                </Typography>
              </Box>

              {/* Feature 2 */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 2, background: 'rgba(239,83,80,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef5350' }}>
                    <AutoGraph sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography sx={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 600, fontFamily: 'Inter' }}>Smart Overdue Tracking</Typography>
                </Box>
                <Typography sx={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, fontFamily: 'Inter' }}>
                  Identify defaults instantly. Our smart algorithms track expected vs. actual collections and flag overdue accounts automatically.
                </Typography>
              </Box>

              {/* Feature 3 */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 2, background: 'rgba(76,175,80,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4caf50' }}>
                    <Group sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography sx={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 600, fontFamily: 'Inter' }}>Customer Portals</Typography>
                </Box>
                <Typography sx={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, fontFamily: 'Inter' }}>
                  Build trust and eliminate disputes. Give your borrowers a secure, live view of their active loans, payment history, and balances.
                </Typography>
              </Box>

              {/* Feature 4 */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: 2, background: 'rgba(156,39,176,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9c27b0' }}>
                    <CloudDone sx={{ fontSize: 18 }} />
                  </Box>
                  <Typography sx={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 600, fontFamily: 'Inter' }}>Cloud Secure & Reliable</Typography>
                </Box>
                <Typography sx={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, fontFamily: 'Inter' }}>
                  Your financial data is heavily encrypted, backed up in real-time, and accessible from anywhere on any device.
                </Typography>
              </Box>
            </Box>

            {/* Pricing Section */}
            <Box sx={{ mb: 8 }}>
              <Typography sx={{ fontSize: { xs: 24, md: 32 }, fontWeight: 800, mb: 1, fontFamily: 'Inter', color: 'var(--text-primary)' }}>
                Simple, transparent pricing.
              </Typography>
              <Typography sx={{ color: 'var(--text-secondary)', fontSize: 16, mb: 4, fontFamily: 'Inter' }}>
                Start for free, upgrade when you need more power.
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4 }}>
                {/* Basic Tier */}
                <Box sx={{ background: 'var(--bg-card)', borderRadius: 4, p: 4, border: '1px solid var(--border-subtle)' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 1, color: 'var(--text-primary)' }}>Basic</Typography>
                  <Typography sx={{ fontSize: 32, fontWeight: 800, mb: 0.5, color: 'var(--text-primary)' }}>
                    ₹0 <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-muted)' }}>/ forever</span>
                  </Typography>
                  <Typography sx={{ color: 'var(--text-secondary)', mb: 4, fontSize: 14 }}>Core features for tracking daily collections.</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                    <FeatureItem text="Unlimited Customers" />
                    <FeatureItem text="Unlimited Accounts" />
                    <FeatureItem text="Daily Collection Tracking" />
                    <FeatureItem text="Basic Profiles" />
                  </Box>
                </Box>

                {/* Pro Tier */}
                <Box sx={{
                  background: 'var(--bg-elevated)', borderRadius: 4, p: 4, border: '2px solid var(--accent-green)',
                  position: 'relative', boxShadow: '0 8px 32px rgba(21,195,88,0.1)'
                }}>
                  <Box sx={{
                    position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                    background: 'linear-gradient(135deg, #15C358, #109441)', color: '#fff', px: 2, py: 0.5,
                    borderRadius: 10, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap'
                  }}>
                    Recommended
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: 20, mb: 1, color: 'var(--accent-green)' }}>Pro</Typography>
                  <Typography sx={{ fontSize: 32, fontWeight: 800, mb: 0.5, color: 'var(--text-primary)' }}>
                    ₹99 <span style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-muted)' }}>/ month</span>
                  </Typography>
                  <Typography sx={{ color: 'var(--text-secondary)', mb: 4, fontSize: 14 }}>Professional analytics and reporting.</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                    <FeatureItem text="Everything in Basic" />
                    <FeatureItem text="Advanced Dashboard Analytics" highlight />
                    <FeatureItem text="Export to Excel" highlight />
                    <FeatureItem text="Professional PDF Reports" highlight />
                    <FeatureItem text="Priority Support" highlight />
                  </Box>
                </Box>
              </Box>
            </Box>
            
            {/* Trust Badges & Links */}
            <Box sx={{ pt: 4, borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleOutline sx={{ color: '#4caf50', fontSize: 16 }} />
                <Typography sx={{ color: '#8892b0', fontSize: 14, fontFamily: 'Inter' }}>Trusted by financiers for daily & weekly collections.</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <a href="/privacy" style={{ color: 'var(--accent-green)', textDecoration: 'none', fontSize: '13px', fontFamily: 'Inter', fontWeight: 500 }}>Privacy Policy</a>
                <a href="/terms" style={{ color: 'var(--accent-green)', textDecoration: 'none', fontSize: '13px', fontFamily: 'Inter', fontWeight: 500 }}>Terms of Service</a>
                <a href="/contact" style={{ color: 'var(--accent-green)', textDecoration: 'none', fontSize: '13px', fontFamily: 'Inter', fontWeight: 500 }}>Contact Us</a>
              </Box>
            </Box>

          </Box>
        </Box>

        {/* Right Side: Login Form */}
        <Box sx={{
          flex: { xs: 'none', md: '0 0 480px' },
          position: { xs: 'static', md: 'sticky' },
          top: 0,
          height: { xs: 'auto', md: '100vh' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 5,
          background: 'var(--bg-card)',
          borderLeft: { xs: 'none', md: '1px solid var(--border-subtle)' },
          boxShadow: { xs: 'none', md: '-10px 0 30px rgba(0,0,0,0.5)' },
          zIndex: 10
        }}>
          <Box sx={{ width: '100%', maxWidth: 320 }}>
            <Typography sx={{ color: 'var(--text-primary)', fontSize: 24, fontWeight: 700, mb: 1, fontFamily: 'Inter' }}>Financier Login</Typography>
            <Typography sx={{ color: 'var(--text-secondary)', fontSize: 14, mb: 4, fontFamily: 'Inter' }}>Please enter your details to sign in to your dashboard.</Typography>

            <form onSubmit={handleEmailAuth} style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '24px' }}>
              {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
              
              <TextField
                fullWidth variant="outlined" placeholder="Email address" type="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                InputProps={{ style: { color: 'var(--text-primary)', background: 'var(--bg-elevated)' } }} size="small"
              />
              <TextField
                fullWidth variant="outlined" placeholder="Password" type="password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                InputProps={{ style: { color: 'var(--text-primary)', background: 'var(--bg-elevated)' } }} size="small"
              />
              <Button
                type="submit" variant="contained" disabled={loading}
                style={{ background: 'linear-gradient(135deg, #15C358 0%, #109441 100%)', color: '#fff', fontWeight: 600, padding: '10px', textTransform: 'none', borderRadius: '8px' }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : (isSignUp ? 'Sign Up' : 'Sign In')}
              </Button>
              
              <Button onClick={() => setIsSignUp(!isSignUp)} style={{ color: 'var(--accent-green)', textTransform: 'none', fontSize: 13 }}>
                {isSignUp ? 'Already have an account? Sign In' : "Don't have an account? Sign Up"}
              </Button>
            </form>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Divider sx={{ flex: 1, borderColor: 'var(--border-subtle)' }} />
              <Typography sx={{ color: 'var(--text-muted)', fontSize: 12 }}>OR</Typography>
              <Divider sx={{ flex: 1, borderColor: 'var(--border-subtle)' }} />
            </Box>

            <Button
              onClick={handleGoogleSignIn} variant="contained" startIcon={<Google style={{ fontSize: 20 }} />}
              style={{
                background: 'var(--text-primary)', color: 'var(--bg-base)', borderRadius: 50, padding: '10px 32px', fontFamily: 'Inter, sans-serif',
                fontWeight: 600, fontSize: 14, textTransform: 'none', width: '100%',
                boxShadow: 'var(--shadow-sm)'
              }}
            >
              Continue with Google
            </Button>

            <Box sx={{ mt: 5, p: 3, background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 4, textAlign: 'center' }}>
              <Typography sx={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 600, fontFamily: 'Inter', mb: 1 }}>
                Are you a customer?
              </Typography>
              <Typography sx={{ color: 'var(--text-secondary)', fontSize: 13, fontFamily: 'Inter', mb: 3 }}>
                Log in with your mobile number to view your active loans and upcoming installments.
              </Typography>
              <Button
                onClick={() => window.location.href = '/customer-login'}
                variant="outlined"
                sx={{
                  color: 'var(--accent-green)', borderColor: 'rgba(21,195,88,0.3)', borderRadius: 50, padding: '8px 24px',
                  fontFamily: 'Inter', fontWeight: 600, textTransform: 'none', width: '100%',
                  '&:hover': { background: 'rgba(21,195,88,0.1)', borderColor: 'var(--accent-green)' }
                }}
              >
                Customer Login
              </Button>
            </Box>

            <Typography sx={{ color: 'var(--text-muted)', fontSize: 11, mt: 4, textAlign: 'center', fontFamily: 'Inter' }}>
              Access is restricted to authorized users only
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const routeModel = [
    {
      path: "/",
      element: !user ? <SignInPage /> : (user.phoneNumber ? <Navigate to="/customer-dashboard" /> : <RequireConsent role="financier"><SidebarLayout user={user} /></RequireConsent>),
      children: [
        { index: true, element: <Home /> },
        { path: "accounts", element: <Accounts /> },
        { path: "customers", element: <Clients /> },
        { path: "dashboard", element: <Dashboard /> },
        { path: "reports", element: <Reports /> },
        { path: "profile", element: <Profile user={user} setUser={setUser} /> },
        { path: "account/:accountId", element: <AccountDetail /> },
        { path: "customers/:customerId", element: <CustomerDetail /> },
        { path: "addCustomer", element: <AddCustomer /> },
        { path: "support", element: <Support /> },
        { path: "subscription", element: <Subscription /> },
        { path: "invoices", element: <Invoices /> },
        { path: "trash", element: <Trash /> },
        { path: "*", element: <div><h2>404</h2></div> }
      ]
    },
    { path: "terms", element: <Terms /> },
    { path: "privacy", element: <PrivacyPolicy /> },
    { path: "contact", element: <ContactUs /> }
  ];

  // Public routes — outside auth layout (no login required)
  const publicRoutes = [
    { path: '/console/c/:clientId', element: <CustomerConsole /> },
    { path: '/customer-login', element: <CustomerLogin /> },
    { path: '/customer-dashboard', element: <RequireConsent role="customer"><CustomerDashboard /></RequireConsent> },
    { path: '/invoice/v/:accountId', element: <InvoicePage /> },
    { path: '/invoice/v/:accountId/:entryId', element: <InvoicePage /> },
  ];

  // Recreate the router only when 'user' state changes so the element closure updates
  const router = React.useMemo(
    () => createBrowserRouter([...routeModel, ...publicRoutes]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user]
  );

  const muiTheme = React.useMemo(() => createTheme({
    palette: {
      mode: theme,
      primary: {
        main: '#1B2A3A',
      },
      secondary: {
        main: '#28884D',
      },
      background: {
        default: theme === 'dark' ? '#101923' : '#f8fafc',
        paper: theme === 'dark' ? '#1B2A3A' : '#ffffff',
      }
    },
    typography: {
      fontFamily: 'Inter, sans-serif',
    }
  }), [theme]);

  return (
    <ThemeProvider theme={muiTheme}>
      <RouterProvider router={router} />
      <CookieConsent />
      <AdBanner />
    </ThemeProvider>
  );
}



export default App;
